<template>
  <m-pageframe title="{organization-label}: Lightbox URL(s)">
    <v-container v-if="lightboxes != null">
      <m-beta-alert
        code="lightboxes"
        description="<b>Heads up!</b><br/><br/>Lightboxes are still new (i.e. in 'beta'). This feature allows you to create popup forms and messages for your website. We have done our best to test this feature out thoroughly, but it <i>might</i> still have a few bugs.  We are eager to get your feedback."
      />

      <v-row justify="space-between" align="center" class="mt-1 mb-4" dense>
        <v-col cols="12" md="auto">
          <link-back :to="back_route" text="All Lightboxes" />
        </v-col>
      </v-row>

      <v-row justify="space-between" align="center" class="mt-1 mb-4" dense>
        <v-col cols="12" md="8">
          <h1 class="text-h4 font-weight-bold mb-1">Lightbox URLs</h1>
          <p class="text-body-2 text--secondary mb-0">
            Manage the URL rules that determine where your lightboxes appear. Rules are evaluated in
            order from top to bottom.
          </p>
        </v-col>

        <v-col cols="12" md="4" class="text-right">
          <template v-if="lightboxes.urls.length"></template>
          <v-btn
            v-if="$rc.$state.isOrganizationAdmin()"
            color="primary"
            :disabled="!$rc.$state.isOrganizationAdmin()"
            @click="$rc.$state.isOrganizationAdmin() ? onClickCreateUrl({}) : null"
          >
            <v-icon left>mdi-plus</v-icon>
            Add URL Rule
          </v-btn>

          <m-restricted-button
            v-else
            color="primary lighten-2"
            tooltip_text="Only organization admins can perform this action"
            @click="$rc.$state.isOrganizationAdmin() ? onClickCreateUrl({}) : null"
          >
            <v-icon left>mdi-plus</v-icon>
            Add URL Rule
          </m-restricted-button>
        </v-col>
      </v-row>

      <empty-state
        v-if="lightboxes.urls.length === 0"
        icon="mdi-link-variant-off"
        title="No URL Rules Configured"
        description="You haven't configured any URL rules yet. Add your first rule below to start showing lightboxes on your website."
        class="mb-6"
        shaded
      >
        <template #actions>
          <v-btn
            v-if="$rc.$state.isOrganizationAdmin()"
            color="primary"
            :disabled="!$rc.$state.isOrganizationAdmin()"
            @click="$rc.$state.isOrganizationAdmin() ? onClickCreateUrl({}) : null"
          >
            <v-icon left>mdi-plus</v-icon>
            Add URL Rule
          </v-btn>

          <m-restricted-button
            v-else
            color="primary lighten-2"
            tooltip_text="Only organization admins can perform this action"
            @click="$rc.$state.isOrganizationAdmin() ? onClickCreateUrl({}) : null"
          >
            <v-icon left>mdi-plus</v-icon>
            Add URL Rule
          </m-restricted-button>
        </template>
      </empty-state>

      <m-edit-lightbox-url
        v-for="(item, index) in lightboxes.urls"
        :key="index"
        :value="item"
        :index="index"
        :lightboxes="lightboxes"
        @input="onChangeUrl"
        @move-up="onClickMoveUp"
        @move-down="onClickMoveDown"
        @delete="onDelete"
        @create-new="onClickCreateUrl"
      />
    </v-container>
  </m-pageframe>
</template>

<script>
import _ from 'lodash';

export default {
  components: {
    EmptyState: () => import('@/components/global/empty-state.vue'),
  },

  data() {
    return {
      lightboxes: null,
    };
  },

  mounted() {
    this.doUpdate();
  },

  computed: {
    back_route() {
      return {
        name: 'lightbox-search-settings',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      };
    },
  },

  methods: {
    update: _.debounce(function() {
      this.doUpdate();
    }, 751),

    doUpdate() {
      this.$rc.rest({
        spinner: false,
        endpoint: 'GET: /api/v1/lightbox/organization/:organization_id',

        params: {
          organization_id: this.$route.params.organization_id,
        },

        on_success: data => {
          this.lightboxes = data;
        },

        on_error: 'Unable to list lightbox url(s)',
      });
    },

    onClickMoveUp(item) {
      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/lightbox/url/move-up',

        params: {
          organization_id: item.organization_id,
          url_id: item.url_id,
        },

        on_success: data => {
          this.doUpdate();
        },

        on_error: 'Unable to move URL up',
      });
    },

    onClickMoveDown(item) {
      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/lightbox/url/move-down',

        params: {
          organization_id: item.organization_id,
          url_id: item.url_id,
        },

        on_success: data => {
          this.doUpdate();
        },

        on_error: 'Unable to move URL down',
      });
    },

    onChangeUrl(item) {
      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/lightbox/url',

        params: item,

        on_success: data => {
          this.doUpdate();
        },

        on_error: 'Unable to save URL',
      });
    },

    onDelete(item) {
      this.$rc.rest({
        spinner: false,
        endpoint: 'DELETE: /api/v1/lightbox/url',

        params: {
          organization_id: item.organization_id,
          url_id: item.url_id,
        },

        on_success: data => {
          this.doUpdate();
        },

        on_error: 'Unable to delete URL',
      });
    },

    onClickCreateUrl(params) {
      this.$rc.pick({
        title: '',
        content_before: 'Pick a lightbox',
        content_after: null,

        choices: this.lightboxes.settings.map(item => {
          return { text: item.label, value: item.lightbox_id };
        }),
        multiple: false,
        type: 'select',
        select_label: 'Lightbox',
        initial_value: null,
        unset_label: null,

        on_ok: value => {
          this.doCreateUrl(value, params);
        },

        on_cancel: () => {},
      });
    },

    doCreateUrl(lightbox_id, params) {
      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/lightbox/url/create-stub',

        params: {
          organization_id: this.$route.params.organization_id,
          lightbox_id: lightbox_id,
          ...params,
        },

        on_success: data => {
          this.doUpdate();
        },

        on_error: 'Unable to create new url',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  max-width: 84rem;
  margin-bottom: 5rem;
}
</style>
